import { useState, useEffect, useCallback } from 'react';
import { OnboardingConfig, OnboardingStep } from '@/config/onboardingConfig';
import { 
  getOnboardingConfig, 
  saveOnboardingConfig as saveConfigToDb 
} from '@/services/onboardingService';
import { usePermissions } from '@/hooks/usePermissions';

export const useOnboardingConfig = () => {
  const [config, setConfig] = useState<OnboardingConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isAdmin } = usePermissions();

  // טעינת קונפיגורציה מהדאטהבייס
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const dbConfig = await getOnboardingConfig();
        setConfig(dbConfig);
      } catch (error) {
        console.error('שגיאה בטעינת קונפיגורציית האונבורדינג:', error);
        setConfig(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadConfig();
  }, []);

  // שמירת קונפיגורציה לדאטהבייס
  const saveConfig = useCallback(async (newConfig: OnboardingConfig) => {
    if (!isAdmin()) {
      console.error('רק מנהלים יכולים לשמור קונפיגורציה');
      return false;
    }

    try {
      const success = await saveConfigToDb(newConfig);
      if (success) {
        setConfig(newConfig);
      }
      return success;
    } catch (error) {
      console.error('שגיאה בשמירת קונפיגורציית האונבורדינג:', error);
      return false;
    }
  }, [isAdmin]);

  // עדכון שלב קיים
  const updateStep = useCallback(async (stepId: string, updatedStep: Partial<OnboardingStep>) => {
    if (!config) return false;
    const newConfig = {
      ...config,
      steps: config.steps.map(step =>
        step.id === stepId ? { ...step, ...updatedStep } : step
      )
    };
    return saveConfig(newConfig);
  }, [config, saveConfig]);

  // הוספת שלב חדש
  const addStep = useCallback(async (newStep: OnboardingStep) => {
    if (!config) return false;
    const newConfig = {
      ...config,
      steps: [...config.steps, newStep],
      totalSteps: config.totalSteps + 1
    };
    return saveConfig(newConfig);
  }, [config, saveConfig]);

  // מחיקת שלב
  const removeStep = useCallback(async (stepId: string) => {
    if (!config) return false;
    const newConfig = {
      ...config,
      steps: config.steps.filter(step => step.id !== stepId),
      totalSteps: Math.max(0, config.totalSteps - 1)
    };
    return saveConfig(newConfig);
  }, [config, saveConfig]);

  // שינוי סדר השלבים
  const reorderSteps = useCallback(async (newOrder: string[]) => {
    if (!config) return false;
    const reorderedSteps = newOrder.map((stepId, index) => {
      const step = config.steps.find(s => s.id === stepId);
      return step ? { ...step, order: index } : null;
    }).filter(Boolean) as OnboardingStep[];

    const newConfig = {
      ...config,
      steps: reorderedSteps
    };
    return saveConfig(newConfig);
  }, [config, saveConfig]);

  // ייצוא קונפיגורציה
  const exportConfig = useCallback((): string => {
    return JSON.stringify(config, null, 2);
  }, [config]);

  // ייבוא קונפיגורציה
  const importConfig = useCallback(async (configJson: string): Promise<boolean> => {
    try {
      const importedConfig = JSON.parse(configJson);
      // בדיקת תקינות בסיסית
      if (importedConfig.steps && Array.isArray(importedConfig.steps)) {
        return saveConfig(importedConfig);
      }
      return false;
    } catch (error) {
      console.error('שגיאה בייבוא קונפיגורציה:', error);
      return false;
    }
  }, [saveConfig]);

  // קבלת שלבים פעילים בסדר
  const getActiveSteps = useCallback(() => {
    if (!config) return [];
    return config.steps
      .filter(step => step.isActive)
      .sort((a, b) => a.order - b.order);
  }, [config?.steps]);

  // קבלת שלב לפי מיקום
  const getStepByOrder = useCallback((order: number) => {
    return getActiveSteps().find(step => step.order === order);
  }, [getActiveSteps]);

  // שינוי סטטוס פעיל/לא פעיל של שלב
  const toggleStepActive = useCallback(async (stepId: string) => {
    if (!config) return false;
    const step = config.steps.find(s => s.id === stepId);
    if (step) {
      return updateStep(stepId, { isActive: !step.isActive });
    }
    return false;
  }, [config?.steps, updateStep]);

  return {
    config,
    isLoading,
    updateStep,
    addStep,
    removeStep,
    reorderSteps,
    toggleStepActive,
    exportConfig,
    importConfig,
    getActiveSteps,
    getStepByOrder,
    saveConfig
  };
};
